import { Constraint } from "@core/model/constraint.enum";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { CampaignFormRule, CampaignFormSchema } from "@factory/form/form.rule";
import { useFormDefaultCampaign } from "@factory/form/form-default-campaign.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseCampaignModel } from "@factory/model/query-model-campaign";
import {
  useQyDeleteCampaign,
  useQyGetCampaignById,
  useQyUpdateCampaign,
} from "@factory/query/campaign.query";
import { viewAsset } from "@factory/query/google-cloud-storage.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditCampaign() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const { formatDateForInput } = useDateTimeContext();
  const { getDefault } = useFormDefaultCampaign();
  const navigate = useNavigate();
  const { mapUpdateCampaign, mapDeleteCampaign } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { campaignId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Campaign
  const handleGetApiSuccess = async (data: GetResponseCampaignModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("tenant", responseData?.tenant || "");
    setValue("level", responseData?.level || "");
    setValue("name", responseData?.name || "");
    setValue("description", responseData?.description || "");
    setValue("image", responseData?.image || "");
    setValue("points", responseData?.points);
    setValue("stamp", responseData?.stamp);
    setValue("redeemByPoints", Boolean(responseData?.redeem_by_points));
    setValue("redeemByStamp", Boolean(responseData?.redeem_by_stamp));
    setValue("multiRedeem", Boolean(responseData?.multi_redeem));
    setValue("redeemLimit", responseData?.redeem_limit);
    setValue("limitPerMonth", responseData?.limit_per_month);
    setValue("constraint", responseData?.constraint);
    if (responseData?.start_date) {
      setValue(
        "startDate",
        formatDateForInput(responseData?.start_date) as any,
        undefined
      );
    }
    if (responseData?.end_date) {
      setValue(
        "endDate",
        formatDateForInput(responseData?.end_date) as any,
        undefined
      );
    }

    if (responseData?.image) {
      const assetFile = await viewAsset(responseData?.image);
      const blobFile = URL.createObjectURL(assetFile);
      setValue("file", blobFile);
    }
  };
  const {
    data: campaignResponse,
    isLoading,
    isError: campaignError,
  } = useQyGetCampaignById(campaignId || "", !!campaignId, handleGetApiSuccess);
  const campaignData = campaignResponse?.data?.[0];

  // API UPDATE Campaign
  const handleUpdateApiSuccess = () => {
    showSuccess("Campaign updated successfully");
  };
  const { mutate: updateCampaign, isLoading: isEditLoading } =
    useQyUpdateCampaign(handleUpdateApiSuccess);

  // API DELETE Campaign
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Campaign deleted successfully");
  };
  const { mutate: deleteCampaign } = useQyDeleteCampaign(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<CampaignFormSchema>({
    defaultValues: getDefault(campaignData),
    resolver: zodResolver(CampaignFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: CampaignFormSchema) => {
    if (!campaignData || !campaignData.code) {
      throw new Error("No campaign data");
    }
    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };

    const formData = mapUpdateCampaign(updatedForm, campaignData.code);
    updateCampaign(formData);
  };
  const handleValidateError = (err: FieldErrors<CampaignFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!campaignData || !campaignData.code) {
      throw new Error("No campaign data found");
    }

    const form = mapDeleteCampaign(campaignData.code);
    deleteCampaign(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    campaignId,
    campaignData,
    isLoading,
    campaignError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
